import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { Globals } from '@config/global';
import { getDataCampaign } from '@utils/get-campaign';
import AdvisorClaudiaUribe from '@assets/json/advisor_claudia_uribe.json';
import { Subject, takeUntil } from 'rxjs';
import { ScreenService } from '@utils/screen';

@Component({
  selector: 'app-attended-by-coach',
  standalone: true,
  imports: [CommonModule, FormsModule, ScrollingModule],
  template: `
    <section
      [ngClass]="{
        'mx-4': resolution === 1,
        'mx-0 px-3': resolution === 2,
      }"
    >
      <div class="row" *ngIf="allowedBrands.includes(getCampaign?.name)">
        <hr class="my-3" />
        <div class="col-12 position-relative advisor_tienda" [ngClass]="{ 'mb-3': wasAttendedStore }">
          <span class="fw-bold text-size-nl text-blue-180 d-block mb-3"
            >¿Ya tienes un coach financiero desde Money Mindset Solutions?</span
          >

          <div class="d-flex gap-3">
            <button
              *ngFor="let option of coachOptions"
              type="button"
              class="btn rounded-pill py-2 px-0 border-1 d-flex align-items-center justify-content-center text-size-nl"
              [ngClass]="{
                'bg-blue-100 border-blue-100 text-white': wasAttendedStore === option.value,
                'bg-transparent border-blue-100 text-dark': wasAttendedStore !== option.value,
              }"
              (click)="selectedOptionsAdvisor(option.value)"
            >
              <span
                class="fw-semibold"
                [ngClass]="{
                  'text-white': wasAttendedStore === option.value,
                  'text-dark': wasAttendedStore !== option.value,
                }"
              >
                {{ option.label }}
              </span>

              <i
                class="bi fw-bold ps-3"
                [ngClass]="{
                  'bi-check-circle-fill text-white': wasAttendedStore === option.value,
                  'bi-circle text-blue-100': wasAttendedStore !== option.value,
                }"
              ></i>
            </button>
          </div>
        </div>
        <div class="col-12 advisor-question position-relative pt-3" *ngIf="wasAttendedStore">
          <span class="fw-bold text-size-nl text-blue-180 d-block mb-2">¿Cuál es tu coach financiero?</span>

          <!-- Search Input -->
          <div class="position-relative">
            <div class="input-group">
              <span class="input-group-text bg-white border-end-0 rounded-start-3">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control border-start-0 rounded-end-3 py-3"
                placeholder="Busca y selecciona tu coach..."
                [(ngModel)]="searchTerm"
                (input)="filterAdvisors()"
                (focus)="openDropdown()"
                (blur)="onInputBlur()"
                #searchInput
                [value]="selectedAdvisor ? selectedAdvisor.name : searchTerm"
              />
            </div>

            <!-- Dropdown List -->
            <div
              class="dropdown-menu w-100 shadow-lg border-0 rounded-3 mt-2 p-0 dropdown-menu-custom position-absolute"
              [class.show]="isDropdownOpen"
            >
              <!-- Virtual Scrolling List -->
              <cdk-virtual-scroll-viewport itemSize="10" class="advisor-viewport" *ngIf="filteredAdvisors.length > 0">
                <div
                  *cdkVirtualFor="let advisor of filteredAdvisors; trackBy: trackByFn"
                  class="dropdown-item border-0 mb-1 py-3 d-flex align-items-center"
                  [ngClass]="{
                    'bg-blue-100 text-white': selectedAdvisor?.email === advisor.email,
                    'bg-hover-blue-10': selectedAdvisor?.email !== advisor.email,
                  }"
                  (mousedown)="selectAdvisor(advisor)"
                  role="button"
                >
                  <div class="me-3">
                    <i
                      class="bi bi-person-circle text-size-nl"
                      [ngClass]="{
                        'text-white': selectedAdvisor?.email === advisor.email,
                        'text-secondary': selectedAdvisor?.email !== advisor.email,
                      }"
                    ></i>
                  </div>
                  <div class="flex-grow-1">
                    <div
                      class="fw-semibold"
                      [ngClass]="{
                        'text-white': selectedAdvisor?.email === advisor.email,
                        'text-dark': selectedAdvisor?.email !== advisor.email,
                      }"
                    >
                      {{ advisor.name }}
                    </div>
                  </div>
                  <span *ngIf="selectedAdvisor?.email === advisor.email" class="ms-auto">
                    <i class="bi bi-check-circle-fill text-white"></i>
                  </span>
                </div>
              </cdk-virtual-scroll-viewport>

              <!-- Empty State -->
              <div *ngIf="filteredAdvisors.length === 0" class="text-center py-4 text-muted">
                <i class="bi bi-search text-secondary fs-2 pb-3"></i>
                <div class="fw-semibold">No se encontraron coaches</div>
                <small>Intenta con otro término de búsqueda</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: `
    button {
      width: 90px;
    }

    .btn > i {
      margin: 0 !important;
    }

    .advisor-viewport {
      height: 240px;
    }

    .dropdown-menu-custom {
      max-height: 400px;
      z-index: 1050;
      top: 100% !important;
    }

    .dropdown-menu {
      animation: fadeIn 0.2s ease;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
})
export class AttendedByCoachComponent {
  @Output() advisorSelected = new EventEmitter<string>();

  public getCampaign: any = {};
  public allowedBrands: string[] = [];
  public mapAdvisor: any = {};
  public wasAttendedStore: boolean | null = null;
  public resolution: number = 0;
  private destroy$ = new Subject<void>();

  // Dropdown state
  public isDropdownOpen = false;
  public searchTerm = '';
  public selectedAdvisor: any = null;
  public filteredAdvisors: any[] = [];

  public coachOptions = [
    { value: true, label: 'Sí' },
    { value: false, label: 'No' },
  ];

  constructor(
    public _getDataCampaign: getDataCampaign,
    public globals: Globals,
    private _screenService: ScreenService,
  ) {
    this.getCampaignData();
    this.appendBrandsAllowed();
    this.appendMapAdvisor();
    this.isMobileSubscribe();
  }

  ngOnInit() {
    this.initializeAdvisors();
  }

  getCampaignData() {
    this._getDataCampaign.getCampaignDataUpdates().subscribe((data) => {
      this.getCampaign = data;
    });
  }

  appendBrandsAllowed() {
    this.allowedBrands = [this.globals.CAMPAIGN_CLAUDIAURIBE];
  }

  appendMapAdvisor() {
    this.mapAdvisor = {
      [this.globals.CAMPAIGN_CLAUDIAURIBE]: AdvisorClaudiaUribe,
    };
  }

  selectedOptionsAdvisor(value: boolean) {
    this.wasAttendedStore = value;

    if (!this.wasAttendedStore) {
      this.advisorSelected.emit('');
      this.selectedAdvisor = null;
      this.searchTerm = '';
    }
  }

  /**
   * @description Suscribe al servicio de pantalla para detectar si es móvil.
   * Actualiza la variable `resolution` según el estado del servicio.
   */
  public isMobileSubscribe() {
    this._screenService.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((screen) => {
      this.resolution = screen;
    });
  }

  /**
   * @description Emite el teléfono del advisor seleccionado al componente padre
   */
  public onAdvisorChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const advisoremail = target.value;
    if (advisoremail) {
      this.advisorSelected.emit(advisoremail);
    }
  }

  /**
   * @description Inicializa la lista de advisors filtrada
   */
  private initializeAdvisors(): void {
    const advisors = this.mapAdvisor[this.getCampaign?.name] || [];
    this.filteredAdvisors = advisors;
  }

  /**
   * @description Abre el dropdown cuando se hace focus en el input
   */
  public openDropdown(): void {
    this.isDropdownOpen = true;
    if (!this.searchTerm && !this.selectedAdvisor) {
      this.filterAdvisors();
    }
  }

  /**
   * @description Maneja el blur del input
   */
  public onInputBlur(): void {
    setTimeout(() => {
      this.isDropdownOpen = false;
    }, 200);
  }

  /**
   * @description Alterna el estado del dropdown
   */
  public toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.searchTerm = '';
      this.filterAdvisors();
    }
  }

  /**
   * @description Filtra los advisors basado en el término de búsqueda
   */
  public filterAdvisors(): void {
    const advisors = this.mapAdvisor[this.getCampaign?.name] || [];
    if (!this.searchTerm.trim()) {
      this.filteredAdvisors = advisors;
    } else {
      const filtered = advisors.filter(
        (advisor: any) =>
          advisor.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          advisor.email.includes(this.searchTerm),
      );
      this.filteredAdvisors = filtered;
    }
  }

  /**
   * @description Selecciona un advisor del dropdown
   */
  public selectAdvisor(advisor: any): void {
    this.selectedAdvisor = advisor;
    this.searchTerm = advisor.name;
    this.isDropdownOpen = false;
    this.advisorSelected.emit(advisor.email);
  }

  /**
   * @description TrackBy function para virtual scrolling
   */
  public trackByFn(index: number, item: any): any {
    return item.email;
  }
}
